/**
 * Video player for background videos.
 * Lazy-loads HLS when the block enters the viewport, respects prefers-reduced-motion,
 * autoplays muted in a loop, and has a pause button (WCAG 2.2.2).
 */
import Hls from 'hls.js';

export function buildHlsUrl(playbackId?: string | null) {
  if (!playbackId) return null;
  return `https://stream.mux.com/${playbackId}.m3u8`;
}

export function shouldAutoplay(prefersReducedMotion: boolean) {
  return !prefersReducedMotion;
}

class MuxVideo {
  // Assigned after the early return for an already decorated node.
  node!: HTMLElement;
  video!: HTMLVideoElement | null;
  toggle!: HTMLElement | null;
  src!: string | null;
  hls!: Hls | null;
  loaded!: boolean;
  inView!: boolean;
  autoplay?: boolean;
  observer?: IntersectionObserver;

  constructor(node: HTMLElement) {
    if (node.decorator instanceof MuxVideo) return;
    node.decorator = this;
    this.node = node;
    this.video = node.querySelector('video');
    this.toggle = node.querySelector('[data-mux-video-toggle]');
    this.src = buildHlsUrl(this.video && this.video.getAttribute('data-hls'));
    this.hls = null;
    this.loaded = false;
    this.inView = false;

    if (!this.video || !this.src) return;

    const reduced = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : true;
    this.autoplay = shouldAutoplay(reduced);

    // Observe the <video>, not this.node: the wrapper is `display: contents`
    // (so the video inherits the image CSS) and therefore generates no box,
    // which means IntersectionObserver would never report it as in view.
    this.observer = new IntersectionObserver(this.onIntersect, {
      threshold: 0.1,
    });
    this.observer.observe(this.video);

    // Drive the toggle state from the real media state, not optimistically.
    this.video.addEventListener('play', this.onPlay);
    this.video.addEventListener('pause', this.onPause);

    if (this.toggle) {
      this.toggle.addEventListener('click', this.onToggle);
    }

    this.setToggle(false);
  }

  onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.inView = true;
        this.load();
        // Once data is buffered this resolves; the manifest/metadata handler
        // below covers the very first load where play() would reject early.
        if (this.autoplay) this.play();
      } else {
        this.inView = false;
        if (this.loaded) this.pause();
      }
    });
  };

  load = () => {
    if (this.loaded) return;
    this.loaded = true;
    if (Hls.isSupported()) {
      this.hls = new Hls({ enableWorker: true, debug: false });
      this.hls.loadSource(this.src!);
      this.hls.attachMedia(this.video!);
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (this.inView && this.autoplay) this.play();
      });
    } else if (this.video!.canPlayType('application/vnd.apple.mpegurl')) {
      this.video!.src = this.src!;
      this.video!.addEventListener('loadedmetadata', () => {
        if (this.inView && this.autoplay) this.play();
      });
    }
  };

  play = () => {
    const p = this.video!.play();
    if (p && p.catch) p.catch(() => {});
  };

  pause = () => {
    this.video!.pause();
  };

  onPlay = () => this.setToggle(true);
  onPause = () => this.setToggle(false);

  onToggle = (e?: Event) => {
    // The media often sits inside a wrapping <a> (offer cards); keep the click
    // from following the link.
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.video!.paused) this.play();
    else this.pause();
  };

  setToggle = (playing: boolean) => {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    this.toggle.setAttribute(
      'aria-label',
      playing ? 'Pause video' : 'Spill video'
    );
    this.node.classList.toggle('is-playing', playing);
  };

  destroy = () => {
    if (this.observer) this.observer.disconnect();
    if (this.hls) this.hls.destroy();
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    if (this.video) {
      this.video.removeEventListener('play', this.onPlay);
      this.video.removeEventListener('pause', this.onPause);
    }
    this.node.decorator = null;
  };
}

export default (node: HTMLElement) => new MuxVideo(node);
